// Positions are normalised (0..1). Sizes are PDF points, multiplied by k
// (pixels per point) so annotations scale correctly with zoom.

export const COLORS = [
  "#e53935",
  "#1565c0",
  "#2e7d32",
  "#111827",
  "#f57c00",
  "#8e24aa",
];
export const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28];
export const MARK_VALUES = [0, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
export const DEFAULT_COMMENTS = [
  "Good",
  "Excellent",
  "Well explained",
  "Incomplete answer",
  "Show your working",
  "Incorrect formula",
  "Units missing",
  "Calculation error",
  "Irrelevant",
  "Be more specific",
];
export const DEFAULT_STAMPS = [
  "Seen",
  "Not attempted",
  "Blank page",
  "Re-check",
  "Repeated answer",
];

export const MIN_SCALE = 0.4;
export const MAX_SCALE = 4;
export const clampScale = (value) =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
export const round2 = (n) => Math.round(n * 100) / 100;
export const TEXT_LINE_HEIGHT = 1.25;

export const uid = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const fontOf = (ann, k) =>
  `${ann.italic ? "italic " : ""}${ann.bold ? "bold " : ""}${(ann.size || 14) * k}px sans-serif`;

let measureCtx;
function measureText(text, font) {
  if (!measureCtx)
    measureCtx = document.createElement("canvas").getContext("2d");
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function pillMetrics(label, k, fontSize) {
  const font = `bold ${fontSize * k}px sans-serif`;
  return {
    font,
    width: measureText(label, font) + 14 * k,
    height: (fontSize + 9) * k,
  };
}

const pillLabel = (ann) =>
  ann.type === "qmark" ? `Q${ann.qNo ?? ""}` : String(ann.text ?? "");

export function drawAnnotation(ctx, ann, width, height, baseK) {
  const k = baseK * (ann.scale || 1);
  const x = (ann.x ?? 0) * width;
  const y = (ann.y ?? 0) * height;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  switch (ann.type) {
    case "highlight":
      ctx.fillStyle = ann.color || "rgba(255, 221, 51, 0.38)";
      ctx.fillRect(x, y, (ann.w || 0.1) * width, (ann.h || 0.05) * height);
      break;

    case "ink":
      if (ann.points?.length) {
        ctx.strokeStyle = ann.color || "#e53935";
        ctx.lineWidth = (ann.size || 2) * k;
        ctx.beginPath();
        ann.points.forEach((pt, i) => {
          if (i === 0) ctx.moveTo(pt.x * width, pt.y * height);
          else ctx.lineTo(pt.x * width, pt.y * height);
        });
        ctx.stroke();
      }
      break;

    case "rect":
    case "oval": {
      const w = (ann.w || 0) * width;
      const h = (ann.h || 0) * height;
      ctx.strokeStyle = ann.color || "#e53935";
      ctx.lineWidth = (ann.size || 2) * k;
      ctx.beginPath();
      if (ann.type === "rect") ctx.rect(x, y, w, h);
      else ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }

    case "line":
    case "arrow": {
      const x2 = ann.x2 * width;
      const y2 = ann.y2 * height;
      const lineWidth = (ann.size || 2) * k;
      ctx.strokeStyle = ann.color || "#e53935";
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      if (ann.type === "arrow") {
        const angle = Math.atan2(y2 - y, x2 - x);
        const head = Math.max(10 * k, lineWidth * 4);
        ctx.moveTo(x2, y2);
        ctx.lineTo(
          x2 - head * Math.cos(angle - 0.45),
          y2 - head * Math.sin(angle - 0.45),
        );
        ctx.moveTo(x2, y2);
        ctx.lineTo(
          x2 - head * Math.cos(angle + 0.45),
          y2 - head * Math.sin(angle + 0.45),
        );
      }
      ctx.stroke();
      break;
    }

    case "text": {
      const size = (ann.size || 14) * k;
      ctx.font = fontOf(ann, k);
      ctx.fillStyle = ann.color || "#e53935";
      String(ann.text ?? "")
        .split("\n")
        .forEach((line, i) => {
          const lineY = y + i * size * TEXT_LINE_HEIGHT;
          ctx.fillText(line, x, lineY);
          if (ann.underline && line) {
            ctx.fillRect(
              x,
              lineY + size * 0.12,
              ctx.measureText(line).width,
              Math.max(1, size * 0.07),
            );
          }
        });
      break;
    }

    case "tick": {
      const s = (ann.size || 22) * k;
      ctx.strokeStyle = ann.color || "#2e7d32";
      ctx.lineWidth = s * 0.15;
      ctx.beginPath();
      ctx.moveTo(x - s * 0.45, y);
      ctx.lineTo(x - s * 0.1, y + s * 0.35);
      ctx.lineTo(x + s * 0.5, y - s * 0.4);
      ctx.stroke();
      break;
    }

    case "cross": {
      const full = (ann.size || 22) * k;
      const s = full * 0.4;
      ctx.strokeStyle = ann.color || "#e53935";
      ctx.lineWidth = full * 0.15;
      ctx.beginPath();
      ctx.moveTo(x - s, y - s);
      ctx.lineTo(x + s, y + s);
      ctx.moveTo(x - s, y + s);
      ctx.lineTo(x + s, y - s);
      ctx.stroke();
      break;
    }

    case "mark": {
      const color = ann.color || "#e53935";
      const label = String(ann.value ?? "");
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = 2 * k;
      ctx.beginPath();
      ctx.arc(x, y, 13 * k, 0, Math.PI * 2);
      ctx.stroke();
      ctx.font = `bold ${(label.length > 2 ? 10 : 14) * k}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, x, y + k * 0.5);
      break;
    }

    case "qmark":
    case "stamp": {
      const isTag = ann.type === "qmark";
      const label = pillLabel(ann);
      const color = ann.color || (isTag ? "#1565c0" : "#8e24aa");
      const {
        font,
        width: w,
        height: h,
      } = pillMetrics(label, k, isTag ? 13 : 12);
      ctx.strokeStyle = color;
      ctx.fillStyle = "rgba(255,255,255,0.86)";
      ctx.lineWidth = 1.5 * k;
      roundedRect(ctx, x, y - h / 2, w, h, 4 * k);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.font = font;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, x + w / 2, y + k * 0.5);
      break;
    }

    default:
      break;
  }
  ctx.restore();
}

export function annotationBounds(ann, width, height, baseK) {
  const k = baseK * (ann.scale || 1);
  const x = (ann.x ?? 0) * width;
  const y = (ann.y ?? 0) * height;

  switch (ann.type) {
    case "highlight":
    case "rect":
    case "oval":
      return { x, y, w: (ann.w || 0.1) * width, h: (ann.h || 0.05) * height };
    case "ink": {
      const xs = (ann.points || []).map((p) => p.x * width);
      const ys = (ann.points || []).map((p) => p.y * height);
      if (!xs.length) return { x, y, w: 0, h: 0 };
      const left = Math.min(...xs);
      const top = Math.min(...ys);
      return {
        x: left,
        y: top,
        w: Math.max(...xs) - left,
        h: Math.max(...ys) - top,
      };
    }
    case "line":
    case "arrow": {
      const x2 = ann.x2 * width;
      const y2 = ann.y2 * height;
      return {
        x: Math.min(x, x2),
        y: Math.min(y, y2),
        w: Math.abs(x2 - x),
        h: Math.abs(y2 - y),
      };
    }
    case "text": {
      const size = (ann.size || 14) * k;
      const lines = String(ann.text ?? "").split("\n");
      const font = fontOf(ann, k);
      return {
        x,
        y: y - size,
        w: Math.max(size, ...lines.map((line) => measureText(line, font))),
        h: lines.length * size * TEXT_LINE_HEIGHT,
      };
    }
    case "tick":
    case "cross": {
      const s = (ann.size || 22) * k * 0.6;
      return { x: x - s, y: y - s, w: s * 2, h: s * 2 };
    }
    case "mark": {
      const r = 14 * k;
      return { x: x - r, y: y - r, w: r * 2, h: r * 2 };
    }
    case "qmark":
    case "stamp": {
      const { width: w, height: h } = pillMetrics(
        pillLabel(ann),
        k,
        ann.type === "qmark" ? 13 : 12,
      );
      return { x, y: y - h / 2, w, h };
    }
    default:
      return { x, y, w: 0, h: 0 };
  }
}

function distanceToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  const t = lengthSq
    ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq))
    : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function hitTest(ann, px, py, width, height, k) {
  const tolerance = 7;

  if (ann.type === "ink" && ann.points?.length) {
    const points = ann.points;
    const reach = tolerance + ((ann.size || 2) * k) / 2;
    if (points.length === 1) {
      return (
        Math.hypot(px - points[0].x * width, py - points[0].y * height) <= reach
      );
    }
    return points
      .slice(1)
      .some(
        (pt, i) =>
          distanceToSegment(
            px,
            py,
            points[i].x * width,
            points[i].y * height,
            pt.x * width,
            pt.y * height,
          ) <= reach,
      );
  }

  if (ann.type === "line" || ann.type === "arrow") {
    return (
      distanceToSegment(
        px,
        py,
        ann.x * width,
        ann.y * height,
        ann.x2 * width,
        ann.y2 * height,
      ) <=
      tolerance + ((ann.size || 2) * k) / 2
    );
  }

  const b = annotationBounds(ann, width, height, k);
  const nearBox =
    px >= b.x - tolerance &&
    px <= b.x + b.w + tolerance &&
    py >= b.y - tolerance &&
    py <= b.y + b.h + tolerance;
  if (!nearBox) return false;

  // Only the outline counts, so a large box never blocks what is inside it.
  if (ann.type === "rect") {
    return (
      Math.min(
        Math.abs(px - b.x),
        Math.abs(px - b.x - b.w),
        Math.abs(py - b.y),
        Math.abs(py - b.y - b.h),
      ) <= tolerance
    );
  }
  if (ann.type === "oval") {
    const rx = b.w / 2 || 1;
    const ry = b.h / 2 || 1;
    const distance = Math.hypot((px - b.x - rx) / rx, (py - b.y - ry) / ry);
    return Math.abs(distance - 1) * Math.min(rx, ry) <= tolerance;
  }
  return true;
}

export function findAnnotationAt(annotations, px, py, width, height, k) {
  for (let i = annotations.length - 1; i >= 0; i -= 1) {
    if (hitTest(annotations[i], px, py, width, height, k))
      return annotations[i];
  }
  return null;
}

export function movedFields(ann, dx, dy) {
  if (ann.type === "ink") {
    return { points: ann.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
  }
  const moved = { x: ann.x + dx, y: ann.y + dy };
  if (ann.type === "line" || ann.type === "arrow") {
    moved.x2 = ann.x2 + dx;
    moved.y2 = ann.y2 + dy;
  }
  return moved;
}

export function resizeHandle(ann, width, height, k) {
  if (ann.type === "ink") return null;
  if (ann.type === "line" || ann.type === "arrow") {
    return { x: ann.x2 * width, y: ann.y2 * height };
  }
  const box = annotationBounds(ann, width, height, k);
  return { x: box.x + box.w + 5, y: box.y + box.h + 5 };
}

export function resizedFields(ann, position, width, height, k) {
  if (ann.type === "rect" || ann.type === "oval" || ann.type === "highlight") {
    return {
      w: Math.max(0.01, position.x - ann.x),
      h: Math.max(0.01, position.y - ann.y),
    };
  }
  if (ann.type === "line" || ann.type === "arrow") {
    return { x2: position.x, y2: position.y };
  }

  const box = annotationBounds(ann, width, height, k);
  const startDistance = Math.hypot(box.w + 5, box.h + 5) || 1;
  const distance = Math.hypot(
    position.x * width - box.x,
    position.y * height - box.y,
  );
  const scale = (ann.scale || 1) * (distance / startDistance);
  return { scale: Math.min(4, Math.max(0.5, scale)) };
}
